import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Button, Stack, Typography } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import logger from '../../app/middleware/log'
import SpinnerBlock from '../../components/Spinner/SpinnerBlock'
import PersonaPicker from './PersonaPicker'
import PlatformBrandedShell from './PlatformBrandedShell'
import { Persona, fetchPersonas } from './personaApi'
import messages from './personaMessages'
import { getActivePersonaUserId, setActivePersonaUserId } from './personaStorage'
import { useSignOut } from './useSignOut'

type GateState =
  | { status: 'pending' }
  | { status: 'ready' }
  | { status: 'picking'; personas: Persona[] }
  | { status: 'noAccess' } // authenticated, but no persona at this subdomain's Tenant
  | { status: 'failed' } //   persona fetch errored (network/timeout/token) — recoverable

// Keep the legacy selectedCustomerTenant key (read by App's sign-in bootstrap) in lockstep with the persona
// key, so a destination origin doesn't carry a stale context from a prior visit. Matches the shape App
// parses and PersonaSwitcher's same-workspace branch writes.
const writeSelectedContext = (p: Persona) =>
  localStorage.setItem(
    'selectedCustomerTenant',
    p.customerId ? `customer-${p.customerId}` : `tenant-${p.tenantId}`,
  )

// E8 F8-4 — persona resolution gate, between MSAL sign-in and the authenticated app. It ensures a persona
// (the X-LogikSpark-Persona-Id the API scopes the request by) is chosen BEFORE any authorized call fires
// (notably App's /user/me). Transparent when not authenticated (App drives the sign-in redirect) or when a
// persona is already set this session. Otherwise it lists the caller's personas and FILTERS to the current
// subdomain's Tenant (a persona at another Tenant can't be used here — the E9 middleware resolves the
// Tenant from the subdomain): 0 -> error, exactly 1 -> auto-select, >=2 -> picker. A ?personaId deep-link
// (cross-subdomain switch, F8-5) auto-selects when it matches one of the caller's personas at this subdomain.
const PersonaGate = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useIsAuthenticated()
  const { instance } = useMsal()
  const { formatMessage } = useIntl()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<GateState>({ status: 'pending' })

  // The cross-subdomain deep-link target (F8-5), resolved AT this (destination) subdomain: by the time
  // the switcher navigates here the persona is local, so it survives the subdomain filter below. Read as
  // a stable string so the effect doesn't re-fire on react-router's per-render URLSearchParams identity.
  const requestedPersonaId = searchParams.get('personaId')
  // Strict parse: an all-digits positive id, else NaN (rejects "22abc"/"22.9"). Used both to decide whether a
  // deep-link should override an already-set persona, and to match it against the caller's personas below.
  const requestedId = requestedPersonaId && /^\d+$/.test(requestedPersonaId) ? Number(requestedPersonaId) : NaN

  // Recomputed each render. NOTE: the transition to transparent is driven by setState({status:'ready'})
  // in the auto-select / picker paths, NOT by this recompute — keep that setState even though it reads as
  // redundant next to the sessionStorage write. (Mount/sign-in events during the blocked window are
  // handled by AuthCallbackPage, which owns its own MSAL event callback, not by the not-yet-mounted App.)
  const activePersonaId = getActivePersonaUserId()
  // A cross-subdomain switch (F8-5) lands here with ?personaId. If it names a DIFFERENT persona than the one
  // already set at this origin (sessionStorage persists across a same-tab revisit), re-resolve instead of
  // honouring the stale key — otherwise the switch silently no-ops and the user keeps the old persona. The
  // resolution block below still validates the id against the caller's OWN personas, so this never selects a
  // foreign identity.
  const deepLinkOverrides = Number.isInteger(requestedId) && requestedId > 0 && requestedId !== activePersonaId
  const personaAlreadySet = activePersonaId != null && !deepLinkOverrides
  const signOut = useSignOut()

  useEffect(() => {
    if (!isAuthenticated || personaAlreadySet) {
      return
    }
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0]
    if (!account) {
      return
    }

    let cancelled = false
    fetchPersonas(instance, account)
      .then((all) => {
        if (cancelled) {
          return
        }
        // Only personas at the Tenant serving this subdomain are usable here (the E9 middleware resolves
        // the Tenant from the host; a foreign-tenant persona would be rejected server-side regardless).
        // Lowercase both sides — hostnames are already lowercase and subdomains are lowercased at signup,
        // but this is cheap insurance against a casing drift that would otherwise hard-lock the user out.
        // (The authed app is always served on a Tenant subdomain; the apex serves only public /signup.)
        const subdomain = window.location.hostname.split('.')[0].toLowerCase()
        const personas = all.filter((p) => p.tenantSubdomain.toLowerCase() === subdomain)

        if (personas.length === 0) {
          setState({ status: 'noAccess' })
          return
        }

        // Honour ?personaId only when it matches one of the caller's OWN personas at this subdomain (never
        // trust an arbitrary value). requestedId is the strict-parsed id from the query (hoisted above).
        const deepLinked = Number.isInteger(requestedId) && requestedId > 0
          ? personas.find((p) => p.userId === requestedId)
          : undefined

        const autoSelect = deepLinked ?? (personas.length === 1 ? personas[0] : undefined)
        if (autoSelect) {
          setActivePersonaUserId(autoSelect.userId)
          writeSelectedContext(autoSelect)
          setState({ status: 'ready' })
          return
        }

        setState({ status: 'picking', personas })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        logger.error('Persona resolution failed:', error)
        setState({ status: 'failed' })
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, personaAlreadySet, instance, requestedId])

  // Transparent: let the app render (App handles the sign-in redirect when unauthenticated).
  if (!isAuthenticated || personaAlreadySet || state.status === 'ready') {
    return <>{children}</>
  }

  if (state.status === 'picking') {
    return (
      <PersonaPicker
        personas={state.personas}
        onPick={(userId) => {
          setActivePersonaUserId(userId)
          const picked = state.personas.find((p) => p.userId === userId)
          if (picked) {
            writeSelectedContext(picked)
          }
          setState({ status: 'ready' })
        }}
      />
    )
  }

  // No persona at this workspace — sign-out is the only sensible recovery (re-checking won't grant access).
  if (state.status === 'noAccess') {
    return (
      <PlatformBrandedShell>
        <Typography variant='h5' component='h1' textAlign='center'>
          {formatMessage(messages.errorTitle)}
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='center'>
          {formatMessage(messages.errorBody)}
        </Typography>
        <Button variant='outlined' onClick={signOut}>
          {formatMessage(messages.signOut)}
        </Button>
      </PlatformBrandedShell>
    )
  }

  // Fetch failed (network/timeout/token) — recoverable: offer a retry (re-runs the gate) + sign-out, so
  // a flaky network doesn't strand the user on a dead screen.
  if (state.status === 'failed') {
    return (
      <PlatformBrandedShell>
        <Typography variant='h5' component='h1' textAlign='center'>
          {formatMessage(messages.failedTitle)}
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='center'>
          {formatMessage(messages.failedBody)}
        </Typography>
        <Stack direction='row' spacing={2}>
          <Button variant='contained' onClick={() => window.location.reload()}>
            {formatMessage(messages.retry)}
          </Button>
          <Button variant='outlined' onClick={signOut}>
            {formatMessage(messages.signOut)}
          </Button>
        </Stack>
      </PlatformBrandedShell>
    )
  }

  return <SpinnerBlock />
}

export default PersonaGate
